import { FC, useState, useCallback, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { Modal, Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ToastMessageContext } from '@context';
import { useTranslation } from 'react-i18next';
import { T_KEYS } from '../../constants';
import { 
  useCheckBookingConnectionMutation,
  useValidateBookingHotelIdsMutation,
  useChannelUnitsForImportQuery,
  useImportUnitsMutation,
} from '../../hooks';
import { BookingUnitType, IHotelValidationResult, IChannelProperty } from '../../types';
import { BookingPropertyIdStep } from '../ConnectionSteps';
import { ImportPropertiesStep } from '../ImportSteps';

type ModalStep = 'propertyId' | 'import';

interface AddBookingPropertyModalProps {
  open: boolean;
  onClose: () => void;
  connectionType: number; // 1 = Single, 2 = Multi
  mainChannelId: number; // Parent account ID for sub-accounts
  onSuccess?: () => void;
}

/**
 * AddBookingPropertyModal Component
 * 
 * Modal for adding a new Booking property to an existing account.
 * Skips channel selection and unit type selection since connectionType is already known.
 * Flow: PropertyIdStep -> ImportPropertiesStep
 */
const AddBookingPropertyModal: FC<AddBookingPropertyModalProps> = ({
  open,
  onClose,
  connectionType,
  mainChannelId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useContext(ToastMessageContext);

  // Modal step state
  const [currentStep, setCurrentStep] = useState<ModalStep>('propertyId');
  
  // Booking property ID state
  const [bookingError, setBookingError] = useState<string | undefined>();
  const [bookingValidationResults, setBookingValidationResults] = useState<IHotelValidationResult[] | undefined>();
  const [hasBookingValidationErrors, setHasBookingValidationErrors] = useState(false);
  
  // Import state
  const [channelId, setChannelId] = useState<string | null>(null);
  const [properties, setProperties] = useState<IChannelProperty[]>([]);
  const [importingIds, setImportingIds] = useState<string[]>([]);

  // Convert connectionType number to BookingUnitType
  const unitType: BookingUnitType = connectionType === 1 ? 'single' : 'multi';

  // Mutations
  const { mutate: checkBookingConnection, isPending: isCheckingBooking } = useCheckBookingConnectionMutation();
  const { mutate: validateBookingHotelIds, isPending: isValidatingBookingIds } = useValidateBookingHotelIdsMutation();
  const { mutate: importUnits, isPending: isImporting } = useImportUnitsMutation();

  // Fetch units for import when channelId is set
  const { 
    data: unitsData, 
    isLoading: isUnitsLoading,
    refetch: refetchUnits,
  } = useChannelUnitsForImportQuery('booking', channelId);

  // Extract userChannelId and channelType from the query result
  const userChannelId = unitsData?.userChannelId ?? null;
  const apiChannelType = unitsData?.channelType ?? null;

  // Transform units data to IChannelProperty when data arrives
  useEffect(() => {
    if (unitsData?.units) {
      setProperties(
        unitsData.units.map((unit) => ({
          id: unit.id,
          externalId: unit.propertyId,
          name: unit.name,
          location: unit.address,
          imageUrl: unit.imageUrl || undefined,
          isImported: unit.isConnected,
          channelName: 'booking' as const,
        }))
      );
    }
  }, [unitsData]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setCurrentStep('propertyId');
      setBookingError(undefined);
      setBookingValidationResults(undefined);
      setHasBookingValidationErrors(false);
      setChannelId(null);
      setProperties([]);
      setImportingIds([]);
    }
  }, [open]);

  // Refetch units on the manage page after successful import
  const handleImportSuccess = useCallback(() => {
    onSuccess?.();
    onClose();
  }, [onSuccess, onClose]);

  // Handle Booking property IDs submission (for single unit type - multiple IDs)
  const handleBookingPropertyIdsSubmit = useCallback((propertyIds: string[]) => {
    setBookingError(undefined);
    setBookingValidationResults(undefined);
    setHasBookingValidationErrors(false);

    validateBookingHotelIds(
      { hotelIds: propertyIds, mainChannelId },
      {
        onSuccess: (data) => {
          if (data.invalidCount > 0) {
            setBookingValidationResults(data.results ?? undefined);
            setHasBookingValidationErrors(true);
            showError(t(T_KEYS.SOME_PROPERTY_IDS_INVALID));
          } else {
            showSuccess(t(T_KEYS.VALIDATION_SUCCESSFUL));
            const hotelId = data.hotelId;
            setChannelId(hotelId);
            setCurrentStep('import');
          }
        },
        onError: (error) => {
          const err = error as Error;
          setBookingError(err.message || t('ERRORS.SOMETHING_WENT_WRONG'));
        },
      }
    );
  }, [validateBookingHotelIds, mainChannelId, showSuccess, showError, t]);

  // Handle Booking single property ID submission (for multi unit type)
  const handleBookingSinglePropertyIdSubmit = useCallback((propertyId: string) => {
    setBookingError(undefined);

    checkBookingConnection(
      { hotelId: propertyId, connectionType, mainChannelId },
      {
        onSuccess: (data) => {
          showSuccess(data.message);
          const hotelId = data.hotelId;
          setChannelId(hotelId || propertyId);
          setCurrentStep('import');
        },
        onError: (error) => {
          const err = error as Error;
          setBookingError(err.message || t('ERRORS.SOMETHING_WENT_WRONG'));
        },
      }
    );
  }, [checkBookingConnection, connectionType, mainChannelId, showSuccess, t]);

  // Handle start import
  const handleStartImport = useCallback((propertyIds: string[]) => {
    if (!userChannelId || apiChannelType === null) {
      showError(t(T_KEYS.CHANNEL_DATA_NOT_AVAILABLE));
      return;
    }

    setImportingIds(propertyIds);

    importUnits(
      { 
        userChannelId, 
        channelType: apiChannelType, 
        listings: unitsData?.units.filter(unit => propertyIds.includes(unit.id)).map(unit => ({
          listingId: unit.id, 
          listingName: unit.name, 
          imageUrl: unit.imageUrl ?? null
        })) || []
      },
      {
        onSuccess: (result) => {
          setImportingIds([]);
          showSuccess(result.message || t('CHANNELS.IMPORT_SUCCESS'));
          handleImportSuccess();
        },
        onError: (error) => {
          setImportingIds([]);
          const err = error as Error;
          showError(err.message || t('ERRORS.SOMETHING_WENT_WRONG'));
        },
      }
    );
  }, [userChannelId, apiChannelType, importUnits, unitsData, showSuccess, showError, t, handleImportSuccess]);

  // Handle import later (close modal)
  const handleImportLater = useCallback(() => {
    onSuccess?.();
    onClose();
  }, [onSuccess, onClose]);


  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="add-booking-property-modal"
      >
        
        <ModalContainer>
          <CloseButton onClick={onClose} size="small">
            <CloseIcon />
          </CloseButton>
          <ModalContent>
            {currentStep === 'propertyId' && (
              <BookingPropertyIdStep
                unitType={unitType}
                onSubmit={handleBookingPropertyIdsSubmit}
                onSubmitSingle={handleBookingSinglePropertyIdSubmit}
                isLoading={unitType === 'single' ? isValidatingBookingIds : isCheckingBooking}
                error={bookingError}
                validationResults={bookingValidationResults}
                hasValidationErrors={hasBookingValidationErrors}
              />
            )}

            {currentStep === 'import' && (
              <ImportPropertiesStep
                channelName="booking"
                properties={properties}
                onStartImport={handleStartImport}
                onImportLater={handleImportLater}
                isLoading={isUnitsLoading}
                importingIds={importingIds}
                isImportButtonLoading={isImporting}
              />
            )}
          </ModalContent>
        </ModalContainer>
      </Modal>
    </>
  );
};

export default AddBookingPropertyModal;

// Styled Components
const ModalContainer = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  background: white;
  border-radius: 24px;
  outline: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 16px;
  inset-inline-start: 16px;
  z-index: 1;
  color: #666666;
  width: 32px;
  height: 32px;
  padding: 4px;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;
